
import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FaClasses } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { CssClassHelper } from "@cervval.js/client-html5-core/src/tools/cssClassHelper";
import { setTitle, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { CjsIcon } from "./icon";
import { IconFaAnimation, IconFaRotate, IconFaSize, refreshIconFaAnimation, refreshIconFaDisplay, refreshIconFaFixedWidth, refreshIconFaInverse, refreshIconFaRotate, refreshIconFaSize } from "./iconFa.tools";


export interface IconFaOptions {

    /** Name of icon (ex fa-calendar, fa-plus, etc.) */
    name: string;

    /** Title of icon */
    title?: string;

    /**
     * Enable fixed width to icon
     * Add a class of fa-fw on the HTML element referencing your icon to set one or more icons to the same fixed width.
     * This is great to use when varying icon widths (e.g. a tall but skinny icon atop a wide but short icon) would throw off vertical alignment.
     * @see https://fontawesome.com/v5.15/how-to-use/on-the-web/styling/fixed-width-icons
     * 
     * Default behavior: fa6: false / fa7: true
     */
    fixedWidth?: boolean;

    /** 
     * Prefix of icon (ex fas, far, fad, ect.), 
     * @default "fas": FaClasses.SOLID (solid) 
     */
    prefix?: FaClasses;

    /** Optional classes to add to icon element */
    classes?: string[];

    /** Add animation to icon */
    animation?: IconFaAnimation;

    /** Add icon rotation */
    rotate?: IconFaRotate;

    /** Size of icon */
    size?: IconFaSize;

    /** Inverse color of icon */
    inverse?: boolean;

    /** 
     * Disable icon, user can't click or interact with icon anymore
     * @default false 
     */
    disabled?: ValueOrFunction<boolean>;

}

export const defaultIconFaOptions: IconFaOptions = {

    /**
     * Name of icon
     * @default - font awesome question icon
     */
    name: "fa-question"

};

@CustomElement({
    name: "cjs-icon-fa",
    style: require("./iconFa.scss"),
    template: require("./iconFa.html")
})
export class CjsIconFa extends CjsIcon {

    /** 
     * Component HTML elements
     */
    @TypedHtmlElements()
    public readonly components!: {
        icon: HTMLElement
    };

    @TypedWebComponentOptions(defaultIconFaOptions)
    public readonly options!: WebComponentOptions<IconFaOptions>;

    /** On click event is triggered */
    @Event(NoBubblesEventInit)
    public clickEvent!: EventHandler<{ originEvent: MouseEvent }>;

    /** Store added css classes from options, use to clean icon classes when refreshing component display */
    protected _iconCssClassHelper = new CssClassHelper(this.components.icon);

    /** get the value of disabled property of options */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    constructor(options?: Partial<IconFaOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }


    /** On init */
    public override init(): void {

        // Add event click listener
        this.addEventListener("click", e => {
            if (this.disabled) { return; }
            this.clickEvent.emit({ originEvent: e });
        });

    }

    /**
     * Component refresh 
     */
    public override refresh(): void {

        // Clear all classes added with options
        this._iconCssClassHelper.clear();

        // Refresh display
        refreshIconFaDisplay(this._iconCssClassHelper, this.options.classes);

        // Refresh Title
        this._refreshTitle();

        // Refresh Name
        this._refreshName();

        // Refresh fixed width
        refreshIconFaFixedWidth(this._iconCssClassHelper, this.options.fixedWidth);

        // Refresh size
        refreshIconFaSize(this._iconCssClassHelper, this.options.size);

        // Refresh animation
        refreshIconFaAnimation(this._iconCssClassHelper, this.options.animation);

        // Refresh rotation
        refreshIconFaRotate(this._iconCssClassHelper, this.options.rotate);

        // refresh inverse
        refreshIconFaInverse(this._iconCssClassHelper, this.options.inverse);

        // Refresh disabled of icon
        this._refreshDisabled();

    }

    /**
     * Set title of icon
     */
    protected _refreshTitle(): void {
        setTitle(this, this.options.title);
    }

    /**
     * Set name of icon
     */
    protected _refreshName(): void {

        const prefixClassName = this.options.prefix ?? FaClasses.SOLID;
        // Add to dynamic classlist 
        this._iconCssClassHelper.add(prefixClassName, this.options.name);
    }

    /**
     * Update disabled state
     */
    protected _refreshDisabled(): void {
        toggleAttribute(this, this.disabled, "disabled", "");
    }
}